import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react';

// Reusable client-side table sorting + optional text search.
// Usage:
//   const { rows, sort, toggle, query, setQuery } = useSortable(items, { searchFields: [(r)=>r.name] });
//   <TableSearch query={query} setQuery={setQuery} count={rows.length} placeholder="Search members" testid="members-search" />
//   <SortTh label="Name" sortKey="name" get={(r)=>r.name} sort={sort} toggle={toggle} .../>
export function useSortable(items, options = {}) {
  const { searchFields, initial = null } = options;
  const [sort, setSort] = useState(initial); // { key, dir, get }
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !searchFields || !searchFields.length) return items;
    return items.filter((it) =>
      searchFields.some((f) => {
        const v = f(it);
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query]);

  const sorted = useMemo(() => {
    if (!sort || !sort.key) return filtered;
    const get = sort.get || ((x) => x[sort.key]);
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = get(a);
      let bv = get(b);
      av = av == null ? '' : av;
      bv = bv == null ? '' : bv;
      let r;
      if (typeof av === 'number' && typeof bv === 'number') {
        r = av - bv;
      } else {
        const na = Number(av);
        const nb = Number(bv);
        if (av !== '' && bv !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) {
          r = na - nb;
        } else {
          r = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
        }
      }
      return sort.dir === 'asc' ? r : -r;
    });
    return arr;
  }, [filtered, sort]);

  const toggle = (key, get) =>
    setSort((s) => (s && s.key === key ? { key, get, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, get, dir: 'asc' }));

  // `sorted` kept as an alias for backward compatibility.
  return { rows: sorted, sorted, sort, toggle, query, setQuery };
}

export function TableSearch({ query, setQuery, placeholder = 'Search…', testid, count }) {
  return (
    <div className="relative inline-flex items-center" data-testid={testid ? `${testid}-wrap` : undefined}>
      <Search size={15} className="absolute left-2.5 pointer-events-none" style={{ color: 'var(--olive)' }} />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="pl-8 pr-8 py-2 rounded border text-sm w-full sm:w-72"
        style={{ borderColor: 'rgba(0,0,0,0.2)' }}
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery('')}
          data-testid={testid ? `${testid}-clear` : undefined}
          className="absolute right-2 p-0.5 rounded hover:bg-black/5"
          title="Clear search"
        >
          <X size={14} style={{ color: 'var(--olive)' }} />
        </button>
      )}
    </div>
  );
}

export function SortTh({ label, sortKey, get, sort, toggle, className = '', style, testid }) {
  const active = sort && sort.key === sortKey;
  return (
    <th
      className={`${className} cursor-pointer select-none`}
      style={style}
      onClick={() => toggle(sortKey, get)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(sortKey, get); } }}
      tabIndex={0}
      role="button"
      data-testid={testid || `sort-${sortKey}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
        ) : (
          <ChevronsUpDown size={13} style={{ opacity: 0.4 }} />
        )}
      </span>
    </th>
  );
}
